import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import DropdownStyled from '@/src/app/components/inputs/StyledInputs/DropdownStyled';
import { useCompanyContext } from '@/src/app/context/CompanyContext';
import { fetchPhaseMasterCostTypes } from '@/src/app/services/accounting/ACFormsService';
import { JCProjectedCost } from '@/src/app/Interfaces/Accounting/JobsContracts.interface';
import { getProjectedCostKey } from '../utils';

type PhaseCostType = { CostType: number; Description: string };

type ProjectedCostTypeCellProps = {
  row: JCProjectedCost;
  usedKeys?: Set<string>;
  onChange: (costType?: number) => void;
  showError?: boolean;
};

const ProjectedCostTypeCell = ({
  row,
  usedKeys,
  onChange,
  showError,
}: ProjectedCostTypeCellProps) => {
  const { selectedCompany } = useCompanyContext();

  const [editing, setEditing] = useState(false);
  const dropdownRef = useRef<Dropdown>(null);

  const costTypesRequest = useQuery({
    queryKey: ['getProjectedPhaseCostTypes', selectedCompany, row.Phase],
    queryFn: ({ signal }) =>
      fetchPhaseMasterCostTypes(selectedCompany!.id, row.Phase, signal),
    refetchOnWindowFocus: false,
    enabled: !!(selectedCompany && row.Phase),
  });

  useEffect(() => {
    if (!editing) {
      return;
    }

    const timer = setTimeout(() => {
      dropdownRef.current?.getFocusInput()?.focus();
    }, 0);

    return () => clearTimeout(timer);
  }, [editing]);

  const hasError = showError && !row.CostType;

  if (!editing && !hasError) {
    const selected = costTypesRequest.data?.find(
      (costType: PhaseCostType) => costType.CostType === row.CostType
    );

    return (
      <div
        className={`w-full ${
          !selected?.Description && 'text-center'
        } cursor-pointer white-space-nowrap overflow-hidden text-overflow-ellipsis totalColor`}
        onClick={() => setEditing(true)}
      >
        {selected?.Description || <i className="pi pi-pencil" />}
      </div>
    );
  }

  return (
    <DropdownStyled
      ref={dropdownRef}
      value={row.CostType || undefined}
      options={costTypesRequest.data}
      labelField="Description"
      valueField="CostType"
      optionDisabled={(option: PhaseCostType) =>
        option.CostType !== row.CostType &&
        (usedKeys?.has(getProjectedCostKey(row.Phase, option.CostType)) ??
          false)
      }
      placeholder={
        costTypesRequest.isError
          ? 'Failed to load cost types!'
          : 'Select Cost Type'
      }
      isDisabled={costTypesRequest.isError || !row.Phase}
      isLoading={costTypesRequest.isFetching}
      clearable={false}
      filter={true}
      resetFilterOnHide={true}
      showOnFocus
      onChange={(e) => {
        onChange(e.value);
        setEditing(false);
      }}
      onHide={() => setEditing(false)}
      error={hasError}
    />
  );
};

export default ProjectedCostTypeCell;
